import React from "react";
import {
  StepperContext,
  type ExistingDocument,
  type StepperContextApiMeta,
} from "./use-stepper";

type StepperSubscriber = (cb: (step: number) => void) => () => void;

type StepperContextProviderProps = {
  children: React.ReactNode;
  step: number;
  /** When set, the flow uploads a new version of this document. */
  existingDocument?: ExistingDocument | null;
};

function initialMeta(
  existingDocument: ExistingDocument | null,
): StepperContextApiMeta {
  return {
    file: null,
    uploadResult: null,
    assetName: null,
    deployment: null,
    toolset: null,
    existingDocument,
  };
}

export const StepperContextProvider: React.FC<StepperContextProviderProps> = ({
  step: initialStep,
  existingDocument = null,
  children,
}) => {
  const [state, setState] = React.useState<"idle" | "completed" | "error">(
    "idle",
  );

  const meta = React.useRef<StepperContextApiMeta>(
    initialMeta(existingDocument),
  );

  const steps = React.useRef<Set<number>>(new Set());
  const step = React.useRef(initialStep);
  const subscribers = React.useRef<Set<(step: number) => void>>(new Set());
  const isMounted = React.useRef(true);

  const registerStep = React.useCallback((step: number) => {
    steps.current.add(step);
  }, []);

  React.useEffect(() => {
    isMounted.current = true;
    const subs = subscribers.current;
    return () => {
      isMounted.current = false;
      subs.clear();
    };
  }, []);

  /**
   * Subscribe to step changes. The callback is called immediately with the
   * current step.
   */
  const subscribe = React.useCallback<StepperSubscriber>((cb) => {
    subscribers.current.add(cb);
    cb(step.current);
    return () => {
      subscribers.current.delete(cb);
    };
  }, []);

  const next = React.useCallback(() => {
    // Prevent state updates if called after unmount (eg: from a lingering async operation)
    if (!isMounted.current) return;

    const nextStep = step.current + 1;
    if (nextStep > Math.max(...Array.from(steps.current))) return;
    step.current = nextStep;
    subscribers.current.forEach((cb) => cb(step.current));
  }, []);

  const reset = React.useCallback(() => {
    step.current = initialStep;
    setState("idle");
    meta.current = initialMeta(existingDocument);
    subscribers.current.forEach((cb) => cb(step.current));
  }, [initialStep, existingDocument]);

  return (
    <StepperContext.Provider
      value={{
        get step() {
          return step.current;
        },
        state,
        setState,
        subscribe,
        registerStep,
        next,
        reset,
        meta,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};
